import axios from "axios";
import CryptoJS from "crypto-js";

/**
 * 获取访问密钥，支持 SSR 环境
 */
function getAccessKey(): string {
  if (typeof localStorage !== "undefined") {
    return localStorage.getItem("CTYUNAK") || "9XCW5FIP7RX85ZZ9FJDD";
  }
  return "9XCW5FIP7RX85ZZ9FJDD";
}

/**
 * 获取签名密钥，支持 SSR 环境
 */
function getSecretKey(): string {
  if (typeof localStorage !== "undefined") {
    return (
      localStorage.getItem("CTYUNSK") ||
      "qTBbJjIo8RZmDMqAN7OQlg1O13j6R5OjYx40nLrK"
    );
  }
  return "qTBbJjIo8RZmDMqAN7OQlg1O13j6R5OjYx40nLrK";
}

const AK = getAccessKey();
const SK = getSecretKey();

/**
 * 获取UNIX时间戳，精确到秒
 */
function getUnixTimestamp() {
  return Math.floor(Date.now() / 1000);
}

/**
 * 将UNIX时间戳转成YYYY-MM-DD格式
 */
function formatTimestamp(timestamp: number) {
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * 拼接规范请求串
 * @param HTTPRequestMethod 请求方法
 * @param CanonicalURI 请求 URI
 * @param CanonicalQueryString 请求参数
 * @param CanonicalHeaders
 * @param SignedHeaders
 * @param body 请求体
 * @param timestamp 时间戳
 */
function sign(
  HTTPRequestMethod: "GET" | "POST",
  CanonicalURI: string,
  CanonicalQueryString: string = "",
  CanonicalHeaders: string | Record<string, any> = "",
  SignedHeaders: string | number = "",
  body: Record<string, any> | null,
  timestamp: number,
) {
  // 拼接规范请求串
  const HashedRequestPayload = CryptoJS.SHA256(
    body ? JSON.stringify(body) : "",
  ).toString(CryptoJS.enc.Hex);
  const CanonicalRequest = `${HTTPRequestMethod}\n${CanonicalURI}\n${CanonicalQueryString}\n${CanonicalHeaders}\n\n${SignedHeaders}\n${
    HashedRequestPayload
  }`;
  // console.log("\n--- CanonicalRequest ---");
  // console.log(CanonicalRequest);

  // 拼接待签名字符串
  const Algorithm = "CT-HMAC-SHA256";
  const RequestTimestamp = timestamp;
  const CredentialScope = `${formatTimestamp(timestamp)}/vss`;
  const HashedCanonicalRequest = CryptoJS.SHA256(CanonicalRequest).toString(
    CryptoJS.enc.Hex,
  );
  const StringToSign = `${Algorithm}\n${RequestTimestamp}\n${CredentialScope}\n${HashedCanonicalRequest}`;
  // console.log("\n--- StringToSign ---");
  // console.log(StringToSign);

  // 计算签名
  // 第一步：计算SecretDate
  const dateString = formatTimestamp(timestamp);
  const secretKey = `CT${SK}`;
  const SecretDate = CryptoJS.HmacSHA256(dateString, secretKey);
  // console.log("\n--- SecretDate ---");
  // console.log(SecretDate.toString(CryptoJS.enc.Hex));

  // 第二步：计算SigningKey
  const SigningKey = CryptoJS.HmacSHA256("vss", SecretDate);
  // console.log("\n--- SigningKey ---");
  // console.log(SigningKey.toString(CryptoJS.enc.Hex));

  // 第三步：计算最终签名
  const Signature = CryptoJS.HmacSHA256(StringToSign, SigningKey).toString(
    CryptoJS.enc.Hex,
  );
  // console.log("\n--- Signature ---");
  // console.log(Signature);

  // 构建Authorization头
  return `${Algorithm} Credential=${AK}/${CredentialScope}, SignedHeaders=${SignedHeaders}, Signature=${Signature}`;
}

export async function ctyunRequest(
  method: "GET" | "POST",
  url: string,
  params?: {
    query?: Record<string, any> | null;
    body?: Record<string, any> | null;
  },
): Promise<any> {
  const timestamp = getUnixTimestamp();
  // const timestamp = 1756374060;
  let queryString = "";
  let uri = url;
  if (params && params?.query) {
    const temp = params.query;
    queryString = Object.keys(temp)
      .map((key) => `${key}=${temp[key]}`)
      .join("&");
    uri = `${uri}?${queryString}`;
  }
  const Authorization = sign(
    method,
    url,
    queryString,
    `host:vssapi.ctyun.cn\ntimestamp:${timestamp}`,
    "host;timestamp",
    params?.body || null,
    timestamp,
  );
  return new Promise((resolve, reject) => {
    axios({
      method,
      url: `/ctyun${uri}`,
      headers: {
        Authorization,
        Version: "2021-11-25",
        Timestamp: timestamp,
      },
      data: params?.body,
    })
      .then((res) => {
        // console.log(res);
        resolve(res.data);
      })
      .catch((err) => {
        // console.log(err);
        if (err.status === 403) {
          resolve(err.response?.data);
        } else {
          reject(err.response?.data);
        }
      });
  });
}

/**
 * 通过设备ID获取播放流地址
 */
export async function GetCtyunDeviceVideoUrl(
  deviceId: string,
): Promise<string> {
  try {
    // 参数校验
    if (!deviceId) {
      console.warn("Device ID is required");
      return "";
    }

    const result = await ctyunRequest(
      "GET",
      `/devices/${deviceId}/live/stream`,
      {
        query: {
          StreamNum: 1,
        },
      },
    );
    console.log(result);
    // 检查响应状态
    if (!result || result.statusCode !== 200) {
      console.error("Failed to get device video stream", result);
      return "";
    }

    // 安全地提取播放URL
    const liveStream = result.returnObj?.LiveStreams?.[0];
    const flvUrl = liveStream?.PlayUrl?.FlvUrl;

    console.log(flvUrl);
    if (flvUrl) {
      return flvUrl;
    }

    console.warn("No FLV URL found in response", result);
    return "";
  } catch (error) {
    console.error("Error getting device video URL:", error);
    return "";
  }
}

/**
 * 获取语音对讲WebSocket地址
 */
export async function GetCtyunDeviceAudioUrl(deviceId: string): Promise<any> {
  try {
    // 参数校验
    if (!deviceId) {
      console.warn("Device ID is required");
      return {};
    }

    const result = await ctyunRequest(
      "POST",
      `/devices/${deviceId}/startTalk`,
      {
        body: {
          // 传输优先级 tcp/udp
          TransPriority: "tcp",
          // 接入协议
          InProtocol: "gb28181",
        },
      },
    );
    console.log(result);
    // 检查响应状态
    if (!result || result.statusCode !== 200) {
      console.error("Failed to get device audio stream", result);
      return {};
    }
    if (result?.returnObj) {
      return result?.returnObj;
    }

    console.warn("No FLV URL found in response", result);
    return {
      audioUrl: "",
      audioKey: "",
    };
  } catch (error) {
    console.error("Error getting device audio URL:", error);
    return {
      audioUrl: "",
      audioKey: "",
    };
  }
}
